import {axiosFactory} from '../../../../client';
import {
  AdmissionExamLevelResponse,
  CandidateRegisteredExamListingResponse,
  CandidateRegisteredExamResponse,
  CandidateTransactionHistoryListingResponse,
  CreateExamAmendmentRequest,
  CreateExamRegistrationRequest,
  CreateExamRegistrationResponse,
  CreateExamWithdrawalRequest,
  CreateNonExamRegistrationRequest,
  DraftRequest,
  ExamCandidateDetails,
  ExamDownloadSummaryResponse,
  ExamProceedToPaymentRequestBody,
  ExamProceedToPaymentResponseBody,
  ExamRegisteredSummaryResponse,
  ExamRegistrationEligibilityResponse,
  ExamRegistrationResponse,
  ExamReviewResponse,
  ExamSaveSubjectRegistrationDraftRequest,
  ExamSubjectAmendmentDraftRequest,
  ExamSubjectsListResponseBody,
  ExamTransactionInfo,
  GetWithdrawalDraftResponse,
  GetWithdrawalResponse,
  NonExamRegistrationEligibilityResponse,
  ParentParticularDetails,
  PaymentLinkRequest,
  PutWithdrawalDetailsRequestData,
  SearchExamSubjectsListSchema,
  SubjectAmendmentReviewResponse,
  SubjectAmendmentSummaryResponse,
  SubjectRegistrationDraftRequest,
  TokenOnlyRequest,
  UpdateSubjectAmendmentDraftRequest,
  UpdateSubjectRegistrationDraftRequest,
  ViewSelectedSubjectsResponseBody,
  WithdrawalPostUploadDocumentsRequest,
  WithdrawalReason,
  WithdrawalSubmittedDetails,
} from '../../model';
import * as ROUTES from './routes';
import {candidateClient} from '../../../audit/@opadmin/api-services/api';
import {KeyValueResponse} from '../../../../model';
import {ApiPathParam} from '../../../../../models/SearchParamSchema';

const baseURL = `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/exam-registrations`;
const nonNationalBaseURL = `${
    import.meta.env.VITE_APP_API_DOMAIN
}/candidate/api/non-national-exam-registrations`;
const subjectBaseURL = `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/subjects`;
const amendmentBaseURL = `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/amendment`;
const examSeriesBaseURL = `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/exam-series`;
const examWithdrawalBaseURL = `${
    import.meta.env.VITE_APP_API_DOMAIN
}/candidate/api/exam-withdrawal-reasons`;
const applicationBaseURL = `${
    import.meta.env.VITE_APP_API_DOMAIN
}/candidate/api/application-attached-files`;

const timeout = 10000;

export const examCandidateClient = axiosFactory(baseURL, timeout);
export const nonNationalExamCandidateClient = axiosFactory(nonNationalBaseURL, timeout);
export const subjectClient = axiosFactory(subjectBaseURL, timeout);
export const amendmentClient = axiosFactory(amendmentBaseURL, timeout);
export const examSeriesClient = axiosFactory(examSeriesBaseURL, timeout);
export const examWithdrawalClient = axiosFactory(examWithdrawalBaseURL, timeout);
export const applicationClient = axiosFactory(applicationBaseURL, timeout);

/** get registered exam listing resit purpose
 * */
export const getRegisteredExam = () =>
    examCandidateClient.get<CandidateRegisteredExamResponse[]>(ROUTES.GET_REGISTERED_EXAM);

export const getPendingExamRegistration = (params: CreateExamRegistrationRequest) =>
    examCandidateClient.get<ExamRegistrationEligibilityResponse>(
        ROUTES.GET_PENDING_REGISTRATION,
        params,
        {timeout: 30000}
    );

export const getExamTypes = () => candidateClient.get<KeyValueResponse[]>(ROUTES.GET_EXAM_TYPES);

export const getExamRegistration = (uuid: string) =>
    examCandidateClient.get<ExamRegistrationResponse>(ROUTES.GET_CANDIDATE_EXAM_REGISTRATION(uuid));

export const getExamCandidateProfile = (uuid: string) =>
    examCandidateClient.get<ExamCandidateDetails>(ROUTES.GET_CANDIDATE_EXAM_PROFILE(uuid));

export const postExamRegistrationCandidateProfile = (
    csrfToken: string,
    uuid: string,
    data: PaymentLinkRequest | null
) => {
  if (!data) {
    return examCandidateClient.post<ExamReviewResponse>(
        csrfToken,
        ROUTES.POST_CANDIDATE_EXAM_REGISTRATION_PROFILE(uuid)
    );
  }

  return examCandidateClient.post<ExamReviewResponse>(
      csrfToken,
      ROUTES.POST_CANDIDATE_EXAM_REGISTRATION_PROFILE(uuid),
      data
  );
};

export const postCreateExamRegistration = (
    csrfToken: string,
    data: CreateExamRegistrationRequest
) => {
  if (data.token) {
    return examCandidateClient.post<CreateExamRegistrationResponse>(csrfToken, '', {
      examType: data.examType,
      token: data.token,
    });
  }

  return examCandidateClient.post<CreateExamRegistrationResponse>(csrfToken, '', {
    examType: data.examType,
  });
};

export const postCreateNonExamRegistration = (
    csrfToken: string,
    data: CreateNonExamRegistrationRequest
) => {
  if (data.token) {
    return nonNationalExamCandidateClient.post<CreateExamRegistrationResponse>(
        csrfToken,
        '',
        {examType: data.examType, admissionLevelCode: data.admissionLevelCode, token: data.token},
        {timeout: 30000}
    );
  }

  return nonNationalExamCandidateClient.post<CreateExamRegistrationResponse>(
      csrfToken,
      '',
      {examType: data.examType, admissionLevelCode: data.admissionLevelCode},
      {timeout: 30000}
  );
};

export const postCandidateExamPayment = (
    csrfToken: string,
    payload: ExamProceedToPaymentRequestBody
) =>
    examCandidateClient.post<ExamProceedToPaymentResponseBody>(
        csrfToken,
        ROUTES.POST_CANDIDATE_EXAM_PAYMENT,
        payload,
        {
          timeout: 30000,
        }
    );

export const getExamTransactionInfo = (uuid: string) =>
    examCandidateClient.get<ExamTransactionInfo>(ROUTES.GET_EXAM_TRANSACTION_INFO(uuid));

export const getSelectedSubjects = (uuid: string) =>
    examCandidateClient.get<ViewSelectedSubjectsResponseBody[]>(ROUTES.GET_SELECTED_SUBJECTS(uuid));

export const getRegisteredExamListing = (examUuid: string | undefined) =>
    examCandidateClient.get<CandidateRegisteredExamListingResponse[]>('', {uuid: examUuid});

export const getRegisteredExamSummary = (uuid: string) =>
    examCandidateClient.get<ExamRegisteredSummaryResponse>(
        ROUTES.GET_CANDIDATE_EXAM_REGISTRATION_SUMMARY(uuid)
    );

export const getCandidateExamDownloadSummary = (uuid: string) =>
    examCandidateClient.get<ExamDownloadSummaryResponse>(
        ROUTES.GET_CANDIDATE_EXAM_DOWNLOAD_SUMMARY(uuid)
    );

export const deleteRegisteredExamDraft = (csrfToken: string, uuid: string) =>
    examCandidateClient.delete(csrfToken, ROUTES.DELETE_CANDIDATE_EXAM_REGISTRATION_DRAFT(uuid));

export const postExamSubjects = (csrfToken: string, payload: SearchExamSubjectsListSchema) =>
    subjectClient.post<ExamSubjectsListResponseBody>(
        csrfToken,
        ROUTES.GET_EXAM_SUBJECTS(payload.apiPathParam),
        {
          searchParam: payload.searchParam,
          examLevelUuid: payload.examLevelUuid,
          examSeriesUuid: payload.examSeriesUuid,
          languageUuid: payload.languageUuid,
          examType: payload.examType,
          examRegistrationUuid: payload.examRegistrationUuid,
        }
    );

export const postExamSubjectRegistrationDraft = (
    csrfToken: string,
    payload: ExamSaveSubjectRegistrationDraftRequest
) =>
    examCandidateClient.post(
        csrfToken,
        ROUTES.POST_EXAM_SUBJECT_REGISTRATION_DRAFT(payload.applicationUuid),
        {
          examSeriesUuid: payload.examSeriesUuid,
          mandatoryPapers: payload.mandatoryPapers,
          subjectCode: payload.subjectCode,
          optionalPapers: payload.optionalPapers,
        }
    );

export const putExamSubjectRegistrationDraft = (
    csrfToken: string,
    payload: UpdateSubjectRegistrationDraftRequest
) =>
    examCandidateClient.put(
        csrfToken,
        ROUTES.PUT_EXAM_SUBJECT_REGISTRATION_DRAFT(
            payload.applicationUuid,
            payload.examRegistrationDetailUuid
        ),
        {
          examSeriesUuid: payload.examSeriesUuid,
          optionalPapers: payload.optionalPapers,
        }
    );

export const deleteExamSubjectRegistrationDraft = (
    csrfToken: string,
    payload: SubjectRegistrationDraftRequest
) =>
    examCandidateClient.delete(
        csrfToken,
        ROUTES.DELETE_EXAM_EXAM_SUBJECT_REGISTRATION_DRAFT(
            payload.applicationUuid,
            payload.examRegistrationDetailUuid
        )
    );

export const getSubjectDetail = (applicationUuid: string, subjectCode: string) =>
    examCandidateClient.get(ROUTES.GET_SUBJECT_DETAIL(applicationUuid, subjectCode));

export const postExamLevel = (csrfToken: string, payload: { examType?: string }) =>
    candidateClient.post(csrfToken, ROUTES.POST_EXAM_LEVEL, payload);

export const getExamSeries = () => examSeriesClient.get('');

export const getLanguages = () => candidateClient.get(ROUTES.GET_LANGUAGES);

export const getCandidateRegisteredSubject = (
    applicationUuid: string,
    examRegistrationDetailsUuid: string
) =>
    examCandidateClient.get(
        ROUTES.GET_CANDIDATE_REGISTERED_SUBJECT(applicationUuid, examRegistrationDetailsUuid)
    );

export const getCandidateRegisteredExamAmendment = (applicationUuid: string, subjectCode: string) =>
    amendmentClient.get(ROUTES.GET_CANDIDATE_REGISTERED_EXAM_AMENDMENT(applicationUuid, subjectCode));

export const postCreateExamAmendment = (csrfToken: string, data: CreateExamAmendmentRequest) =>
    amendmentClient.post(csrfToken, '', data);

export const getRegisteredSubjects = (subjectCode: string) =>
    amendmentClient.get(ROUTES.GET_REGISTERED_SUBJECT(subjectCode));

export const putDraftSubjectWithdrawal = (csrfToken: string, data: DraftRequest) =>
    amendmentClient.put(csrfToken, ROUTES.PUT_DRAFT_DELETE, {
      uuid: data.uuid,
      subjectCode: data.subjectCode,
    });

export const getExamLevelAmendment = () => amendmentClient.get(ROUTES.GET_EXAM_LEVEL_AMENDMENT);

export const putUndoDraftSubjectWithdrawal = (csrfToken: string, data: DraftRequest) =>
    amendmentClient.put(csrfToken, ROUTES.PUT_UNDO_DRAFT_DELETE, {
      uuid: data.uuid,
      subjectCode: data.subjectCode,
    });

export const postExamSubjectAmendmentDraft = (
    csrfToken: string,
    payload: ExamSubjectAmendmentDraftRequest
) =>
    amendmentClient.post(csrfToken, ROUTES.POST_SAVE_DRAFT_ADDED_SUBJECT_AMENDMENT, {
      examSeriesUuid: payload.examSeriesUuid,
      mandatoryPapers: payload.mandatoryPapers,
      subjectCode: payload.subjectCode,
      optionalPapers: payload.optionalPapers,
    });

export const putUpdateRegistrationSubjectAmendment = (
    csrfToken: string,
    payload: UpdateSubjectAmendmentDraftRequest
) =>
    amendmentClient.put(
        csrfToken,
        ROUTES.PUT_EXAM_SUBJECT_AMENDMENT_REGISTRATION_DRAFT(
            payload.applicationUuid,
            payload.examRegistrationDetailUuid
        ),
        {
          examSeriesUuid: payload.examSeriesUuid,
          optionalPapers: payload.optionalPapers,
        }
    );

export const getRegisteredAmendmentSummary = (uuid: string) =>
    amendmentClient.get<SubjectAmendmentSummaryResponse>(
        ROUTES.GET_CANDIDATE_EXAM_AMENDMENT_SUMMARY(uuid)
    );

export const getReviewAmendmentSummary = (uuid: string, token?: string) => {
  if (!token) {
    return amendmentClient.get<SubjectAmendmentReviewResponse>(
        ROUTES.GET_CANDIDATE_REVIEW_EXAM_AMENDMENT(uuid)
    );
  }
  return amendmentClient.get<SubjectAmendmentReviewResponse>(
      ROUTES.GET_CANDIDATE_REVIEW_EXAM_AMENDMENT_WITH_TOKEN(uuid, token)
  );
};

export const postCandidateExamAmendmentPayment = (
    csrfToken: string,
    payload: ExamProceedToPaymentRequestBody
) =>
    amendmentClient.post<ExamProceedToPaymentResponseBody>(
        csrfToken,
        ROUTES.POST_CANDIDATE_EXAM_PAYMENT,
        payload,
        {
          timeout: 30000,
        }
    );

export const getCandidateExamAmendmentDownloadSummary = (uuid: string) =>
    amendmentClient.get<ExamDownloadSummaryResponse>(
        ROUTES.GET_CANDIDATE_EXAM_AMENDMENT_DOWNLOAD_SUMMARY(uuid)
    );

export const postSubjectValidation = (csrfToken: string, uuid: string) =>
    examCandidateClient.post(csrfToken, ROUTES.POST_SUBJECT_VALIDATION(uuid));

export const getTransactionHistoryListing = (paging: ApiPathParam) =>
    examCandidateClient.get<CandidateTransactionHistoryListingResponse>(
        ROUTES.GET_TRANSACTION_HISTORY(paging)
    );

export const getWithdrawal = (uuid: string) =>
    examCandidateClient.get<GetWithdrawalResponse>(ROUTES.GET_WITHDRAWAL(uuid));

export const getWithdrawalDropDown = () => examWithdrawalClient.get<WithdrawalReason[]>('');

export const postWithdrawalUploadDocuments = (
    tokenCsrf: string,
    payload: WithdrawalPostUploadDocumentsRequest
) => applicationClient.post<WithdrawalPostUploadDocumentsRequest>(tokenCsrf, '', payload.payload);

export const putUpdateExamWithdrawalDetails = (
    tokenCsrf: string,
    data: PutWithdrawalDetailsRequestData
) =>
    examCandidateClient.put<PutWithdrawalDetailsRequestData>(
        tokenCsrf,
        `${ROUTES.PUT_WITHDRAWAL_DETAILS(data.payload.uuid)}`,
        data.payload
    );

export const postCreateExamWithdrawal = (
  csrfToken: string,
  data: CreateExamWithdrawalRequest,
  params: TokenOnlyRequest
) => {
  const url = ROUTES.POST_CREATE_EXAM_WITHDRAWAL(data.uuid, params?.token ?? undefined);
  return examCandidateClient.post(csrfToken, url, data);
};

export const getWithdrawalDraft = (uuid: string) =>
    examCandidateClient.get<GetWithdrawalDraftResponse>(ROUTES.GET_WITHDRAWAL_DRAFT(uuid));

export const getWithdrawDetails = (uuid: string) =>
    examCandidateClient.get(ROUTES.GET_WITHDRAW_DETAILS(uuid));

export const putConfirmWithdraw = (tokenCsrf: string, uuid: string) =>
    examCandidateClient.put(tokenCsrf, `${ROUTES.PUT_CONFIRM_WITHDRAW(uuid)}`, {});

export const getWithdrawalAcknowledgement = (uuid: string) =>
    examCandidateClient.get<WithdrawalSubmittedDetails>(ROUTES.GET_WITHDRAWAL_ACKNOWLEDGEMENT(uuid));

export const getWithdrawalDownloadPdf = (uuid: string) =>
    examCandidateClient.get<ExamDownloadSummaryResponse>(ROUTES.GET_WITHDRAWAL_DOWNLOAD_PDF(uuid));

export const getSubmittedWithdrawal = (uuid: string) =>
    examCandidateClient.get<GetWithdrawalDraftResponse>(ROUTES.GET_SUBMITTED_WITHDRAWAL(uuid));

export const getExamLevelWithdrawal = (params: TokenOnlyRequest) =>
    examCandidateClient.get(ROUTES.GET_EXAM_LEVEL_WITHDRAWAL(), params);

export const getWithdrawalDraftDateTime = () =>
    examCandidateClient.get(ROUTES.GET_WITHDRAWAL_TIMESTAMP);

export const getPendingNonNationalExamRegistration = (params: CreateExamRegistrationRequest) =>
    nonNationalExamCandidateClient.get<NonExamRegistrationEligibilityResponse>(
        ROUTES.GET_PENDING_REGISTRATION,
        params,
        {timeout: 30000}
    );

export const getAdmissionExamLevel = (examType: string) =>
    nonNationalExamCandidateClient.get<AdmissionExamLevelResponse>(
        ROUTES.GET_ADMISSION_EXAM_LEVEL(examType),
        {},
        {timeout: 30000}
    );

export const getCeqTypes = () =>
    nonNationalExamCandidateClient.get<KeyValueResponse[]>(
        ROUTES.GET_CEQ_TYPES,
        {},
        {timeout: 30000}
    );

export const getCeqRequired = () =>
    nonNationalExamCandidateClient.get<string[]>(ROUTES.GET_CEQ_REQUIRED);

export const getParentParticularProfile = (uuid: string) =>
    nonNationalExamCandidateClient.get<ParentParticularDetails>(
        ROUTES.GET_PARENT_PARTICULAR_PROFILE(uuid),
        {},
        {timeout: 30000}
    );
